// Map "Region reach" overlay (#24 extension): where packets carrying one scope were heard in the last 7 days.
// Pure helpers only; the points come from GET /api/audit/scope-reach (the stored Audit cell, audit/scope_reach.py),
// never computed here.

export const REACH_NONE_LABEL = "None";
export const REACH_CONTROL_LABEL = "Region reach";
export const REACH_CLEAR_LABEL = "Clear";
export const REACH_HEAT_SOURCE = "scope-reach";
export const REACH_HEAT_LAYER = "scope-reach-heat";

/** [lat, lon, packets] */
export type ReachPoint = [number, number, number];

export interface ReachScope {
  name: string;
  repeaters: ReachPoint[];
  observers: ReachPoint[];
  repeater_count: number;
  observer_count: number;
}

export interface ReachPayload {
  pending?: boolean;
  window: string;
  scopes: Record<string, ReachScope>;
}

export function isReachPayload(data: unknown): data is ReachPayload {
  const d = data as ReachPayload | null;
  return !!d && !d.pending && typeof d.scopes === "object" && d.scopes !== null;
}

/** The key a scope has in the payload and in the map-scopes colour list: case-folded, with a leading #. */
export function reachKey(name: string): string {
  const t = (name || "").trim().toLowerCase();
  return t.startsWith("#") ? t : `#${t}`;
}

/** Scopes to pick from: the ones with something to show, most repeaters first, then name. */
export function reachChoices(payload: ReachPayload | null): { key: string; label: string; repeaters: number }[] {
  if (!payload) return [];
  return Object.entries(payload.scopes)
    .filter(([, s]) => s.repeater_count + s.observer_count > 0)
    .map(([key, s]) => ({ key, label: s.name, repeaters: s.repeater_count }))
    .sort((a, b) => b.repeaters - a.repeaters || a.label.localeCompare(b.label));
}

/** The least a point counts. Raised from 0.15 after measuring on real tiles: a lone quiet repeater at 0.15 left the
 * map almost unchanged. */
export const REACH_WEIGHT_FLOOR = 0.35;

/** How strongly one point counts: the square root of its share of the scope's busiest point, never below the floor,
 * so a busy repeater does not drown out the quiet ones that still carried the scope. */
export function pointWeight(packets: number, maxPackets: number): number {
  if (!(maxPackets > 0) || !(packets > 0)) return REACH_WEIGHT_FLOOR;
  return Math.max(REACH_WEIGHT_FLOOR, Math.min(1, Math.sqrt(packets / maxPackets)));
}

export interface ReachGeoJSON {
  type: "FeatureCollection";
  features: { type: "Feature"; geometry: { type: "Point"; coordinates: [number, number] }; properties: Record<string, number | string> }[];
}

/** Heat points: the repeaters and observers that carried the scope, weighted. */
export function reachGeoJSON(scope: ReachScope | null): ReachGeoJSON {
  const features: ReachGeoJSON["features"] = [];
  if (scope) {
    const heat = [...scope.repeaters, ...scope.observers];
    const max = heat.reduce((m, p) => Math.max(m, p[2]), 0);
    for (const [lat, lon, n] of heat) {
      features.push({ type: "Feature", geometry: { type: "Point", coordinates: [lon, lat] }, properties: { kind: "heat", w: pointWeight(n, max) } });
    }
  }
  return { type: "FeatureCollection", features };
}

/** "#ida: heard by 86 repeaters / 15 observers in 7 d" */
export function reachLegendText(name: string, scope: ReachScope): string {
  const r = scope.repeater_count;
  const o = scope.observer_count;
  return `${name}: heard by ${r} repeater${r === 1 ? "" : "s"} / ${o} observer${o === 1 ? "" : "s"} in 7 d`;
}

/** "#rrggbb" -> "rgba(r,g,b,a)" for the heat ramp. */
export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return `rgba(128,128,128,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}


function lum(rgb: [number, number, number]): number {
  const c = rgb.map((v) => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/** The tone range a heat colour must sit in so the shading stands out from its map style: on the pale light tiles
 * (luminance about 0.8) it may not be lighter than REACH_HEAT_MAX_LUM_LIGHT, on the dark re-tinted tiles (about 0.04)
 * not darker than REACH_HEAT_MIN_LUM_DARK. */
export const REACH_HEAT_MAX_LUM_LIGHT = 0.28;
export const REACH_HEAT_MIN_LUM_DARK = 0.22;

/**
 * The colour the heat shading is drawn in: the scope's own colour, darkened a step at a time on the light map or
 * lightened on the dark map until it is in the tone range above. A colour already in range is returned unchanged;
 * the hue stays the scope's, so scopes still tell apart. "#rrggbb" in, "#rrggbb" out (anything else is returned as is).
 */
export function reachHeatColour(hex: string, theme: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  let rgb: [number, number, number] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const dark = theme === "dark";
  const target: [number, number, number] = dark ? [255, 255, 255] : [0, 0, 0];
  for (let i = 0; i < 20; i++) {
    const l = lum(rgb);
    if (dark ? l >= REACH_HEAT_MIN_LUM_DARK : l <= REACH_HEAT_MAX_LUM_LIGHT) break;
    rgb = rgb.map((v, k) => Math.round(v + (target[k] - v) * 0.1)) as [number, number, number];
  }
  return "#" + rgb.map((v) => v.toString(16).padStart(2, "0")).join("");
}
